// ══════════════════════════════════════════════════════════════════════
// GQ Pipeline State — the missing spine
// ──────────────────────────────────────────────────────────────────────
// One global store. Every page reads/writes via window.GQ.state.*
//
//   • currentStrategy        — the active strategy object (id, code, params)
//   • lastBacktestRunId      — most recent successful backtest run UUID
//   • lastOptimizationRunId  — most recent successful optimization run UUID
//   • lastStressRunId        — most recent successful stress run UUID
//   • runs                   — local cache of fetched run objects {id → obj}
//
// Storage layers (cascading, write-through):
//   1. In-memory:   instant reads, subscribers fire on every change
//   2. LocalStorage: survives reloads even when offline (key: gq:pipeline)
//   3. Supabase:    authoritative copy in pipeline_state table; runs in *_runs
//
// URL convention: any page can deep-link to a run via #page?run=<uuid>.
// On mount each page should call GQ.state.hydrateFromURL() which fetches
// the run from Supabase, populates state, and returns it.
// ══════════════════════════════════════════════════════════════════════

(function (root) {
  const LS_KEY = 'gq:pipeline';
  const subscribers = new Set();
  let mem = loadFromLocalStorage() || {
    currentStrategyId: null,
    currentStrategy: null,
    lastBacktestRunId: null,
    lastOptimizationRunId: null,
    lastStressRunId: null,
    runs: {},               // id → run object cache
    aiThreadId: null,
    updatedAt: 0,
  };

  // ────────────────────────────────────────────────────────────────────
  // Persistence helpers
  // ────────────────────────────────────────────────────────────────────
  function loadFromLocalStorage() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function saveToLocalStorage() {
    try { localStorage.setItem(LS_KEY, JSON.stringify(mem)); }
    catch (e) { /* quota, ignore */ }
  }
  function notify(reason) {
    subscribers.forEach(fn => { try { fn(mem, reason); } catch (e) { console.error(e); } });
  }

  // ────────────────────────────────────────────────────────────────────
  // Subscribe / notify
  // ────────────────────────────────────────────────────────────────────
  function subscribe(fn) { subscribers.add(fn); return () => subscribers.delete(fn); }
  function snapshot() { return JSON.parse(JSON.stringify(mem)); }

  // ────────────────────────────────────────────────────────────────────
  // Core setters — every mutation goes through these
  // ────────────────────────────────────────────────────────────────────
  function setCurrentStrategy(strategy) {
    mem.currentStrategy   = strategy || null;
    mem.currentStrategyId = strategy ? strategy.id : null;
    mem.updatedAt = Date.now();
    saveToLocalStorage(); persistPipeline(); notify('currentStrategy');
  }
  function setLastBacktestRun(run) {
    if (!run) return;
    mem.runs[run.id] = run;
    mem.lastBacktestRunId = run.id;
    mem.updatedAt = Date.now();
    saveToLocalStorage(); persistPipeline(); notify('lastBacktestRun');
  }
  function setLastOptimizationRun(run) {
    if (!run) return;
    mem.runs[run.id] = run;
    mem.lastOptimizationRunId = run.id;
    mem.updatedAt = Date.now();
    saveToLocalStorage(); persistPipeline(); notify('lastOptimizationRun');
  }
  function setLastStressRun(run) {
    if (!run) return;
    mem.runs[run.id] = run;
    mem.lastStressRunId = run.id;
    mem.updatedAt = Date.now();
    saveToLocalStorage(); persistPipeline(); notify('lastStressRun');
  }
  function cacheRun(run) {
    if (!run || !run.id) return;
    mem.runs[run.id] = run;
    saveToLocalStorage(); notify('cacheRun');
  }
  function clear() {
    mem = {
      currentStrategyId: null, currentStrategy: null,
      lastBacktestRunId: null, lastOptimizationRunId: null, lastStressRunId: null,
      runs: {}, aiThreadId: null, updatedAt: Date.now(),
    };
    saveToLocalStorage(); persistPipeline(); notify('clear');
  }

  // ────────────────────────────────────────────────────────────────────
  // Getters
  // ────────────────────────────────────────────────────────────────────
  const getCurrentStrategy        = () => mem.currentStrategy;
  const getCurrentStrategyId      = () => mem.currentStrategyId;
  const getLastBacktestRunId      = () => mem.lastBacktestRunId;
  const getLastOptimizationRunId  = () => mem.lastOptimizationRunId;
  const getLastStressRunId        = () => mem.lastStressRunId;
  const getRun                    = (id) => mem.runs[id] || null;

  // ────────────────────────────────────────────────────────────────────
  // Supabase pipeline_state mirror
  // ────────────────────────────────────────────────────────────────────
  async function persistPipeline() {
    const sb = root.sbClient;
    if (!sb) return;
    try {
      const { data: { user } } = await sb.auth.getUser();
      if (!user) return;
      await sb.from('pipeline_state').upsert({
        user_id:                  user.id,
        current_strategy_id:      mem.currentStrategyId,
        last_backtest_run_id:     mem.lastBacktestRunId,
        last_optimization_run_id: mem.lastOptimizationRunId,
        last_stress_run_id:       mem.lastStressRunId,
        updated_at:               new Date().toISOString(),
      });
    } catch (e) { /* offline / no auth — keep going */ }
  }
  async function hydrateFromSupabase() {
    const sb = root.sbClient;
    if (!sb) return null;
    try {
      const { data: { user } } = await sb.auth.getUser();
      if (!user) return null;
      const { data } = await sb.from('pipeline_state').select('*').eq('user_id', user.id).maybeSingle();
      if (!data) return null;
      mem.currentStrategyId      = data.current_strategy_id;
      mem.lastBacktestRunId      = data.last_backtest_run_id;
      mem.lastOptimizationRunId  = data.last_optimization_run_id;
      mem.lastStressRunId        = data.last_stress_run_id;
      // Hydrate the strategy object too
      if (data.current_strategy_id) {
        const { data: s } = await sb.from('strategies').select('*').eq('id', data.current_strategy_id).maybeSingle();
        if (s) mem.currentStrategy = s;
      }
      saveToLocalStorage(); notify('hydrate');
      return mem;
    } catch (e) { return null; }
  }

  // ────────────────────────────────────────────────────────────────────
  // Run CRUD — used by Backtest / Optimize / Stress pages
  // ────────────────────────────────────────────────────────────────────
  async function saveBacktestRun(payload) {
    const sb = root.sbClient;
    const { data: { user } } = sb ? await sb.auth.getUser() : { data: { user: null } };
    const run = {
      id: payload.id || crypto.randomUUID(),
      strategy_id: payload.strategy_id,
      user_id: user?.id || null,
      asset: payload.asset || null,
      timeframe: payload.timeframe || null,
      date_start: payload.date_start || null,
      date_end: payload.date_end || null,
      capital: payload.capital || null,
      fees_bps: payload.fees_bps || null,
      slippage_bps: payload.slippage_bps || null,
      config: payload.config || {},
      metrics: payload.metrics || {},
      trades: payload.trades || [],
      equity_curve: payload.equity_curve || [],
      parent_run_id: payload.parent_run_id || null,
      status: 'complete',
      created_at: new Date().toISOString(),
      finished_at: new Date().toISOString(),
    };
    cacheRun(run);
    setLastBacktestRun(run);
    if (sb && user) {
      try { await sb.from('backtest_runs').insert(run); }
      catch (e) { console.warn('[GQ.state] backtest_run insert failed', e); }
    }
    return run;
  }
  async function saveOptimizationRun(payload) {
    const sb = root.sbClient;
    const { data: { user } } = sb ? await sb.auth.getUser() : { data: { user: null } };
    const run = {
      id: payload.id || crypto.randomUUID(),
      strategy_id: payload.strategy_id,
      user_id: user?.id || null,
      base_backtest_id: payload.base_backtest_id || null,
      objective: payload.objective || 'sharpe',
      generations: payload.generations || 40,
      population_size: payload.population_size || 50,
      best_params: payload.best_params || {},
      best_metrics: payload.best_metrics || {},
      pareto_front: payload.pareto_front || [],
      history: payload.history || [],
      walk_forward_results: payload.walk_forward_results || null,
      status: 'complete',
      progress: 1,
      created_at: new Date().toISOString(),
      finished_at: new Date().toISOString(),
    };
    cacheRun(run);
    setLastOptimizationRun(run);
    if (sb && user) {
      try { await sb.from('optimization_runs').insert(run); }
      catch (e) { console.warn('[GQ.state] optimization_run insert failed', e); }
    }
    return run;
  }
  async function saveStressRun(payload) {
    const sb = root.sbClient;
    const { data: { user } } = sb ? await sb.auth.getUser() : { data: { user: null } };
    const run = {
      id: payload.id || crypto.randomUUID(),
      strategy_id: payload.strategy_id,
      user_id: user?.id || null,
      optimization_run_id: payload.optimization_run_id || null,
      scenarios: payload.scenarios || [],
      results: payload.results || {},
      mc_paths: payload.mc_paths || null,
      risk_metrics: payload.risk_metrics || {},
      regime_analysis: payload.regime_analysis || null,
      factor_attribution: payload.factor_attribution || null,
      ai_grade: payload.ai_grade || null,
      ai_notes: payload.ai_notes || null,
      created_at: new Date().toISOString(),
    };
    cacheRun(run);
    setLastStressRun(run);
    if (sb && user) {
      try { await sb.from('stress_runs').insert(run); }
      catch (e) { console.warn('[GQ.state] stress_run insert failed', e); }
    }
    return run;
  }

  async function fetchRun(table, id) {
    if (mem.runs[id]) return mem.runs[id];
    const sb = root.sbClient;
    if (!sb) return null;
    try {
      const { data } = await sb.from(table).select('*').eq('id', id).maybeSingle();
      if (data) cacheRun(data);
      return data || null;
    } catch (e) { return null; }
  }
  const fetchBacktestRun     = (id) => fetchRun('backtest_runs', id);
  const fetchOptimizationRun = (id) => fetchRun('optimization_runs', id);
  const fetchStressRun       = (id) => fetchRun('stress_runs', id);

  // ────────────────────────────────────────────────────────────────────
  // URL / routing helpers
  // ────────────────────────────────────────────────────────────────────
  // Hash format: #pageName?run=<uuid>&compare=<uuid>
  function parseHash() {
    const h = location.hash.replace(/^#/, '');
    const [page, queryStr = ''] = h.split('?');
    const params = {};
    queryStr.split('&').filter(Boolean).forEach(kv => {
      const [k, v] = kv.split('=');
      params[decodeURIComponent(k)] = decodeURIComponent(v || '');
    });
    return { page, params };
  }
  function setHash(page, params = {}) {
    const qs = Object.entries(params)
      .filter(([_, v]) => v != null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    location.hash = qs ? `${page}?${qs}` : page;
  }
  // On page mount: read URL, fetch the run, populate state, return it
  async function hydrateFromURL() {
    const { page, params } = parseHash();
    if (!params.run) return { page, params, run: null };
    let run = mem.runs[params.run];
    if (!run) {
      // Try each table — we don't know which yet
      run = await fetchBacktestRun(params.run)
         || await fetchOptimizationRun(params.run)
         || await fetchStressRun(params.run);
    }
    return { page, params, run };
  }

  // ────────────────────────────────────────────────────────────────────
  // Serialisation — for AI chat context
  // ────────────────────────────────────────────────────────────────────
  function serialize() {
    const trim = run => run && {
      id: run.id,
      strategy_id: run.strategy_id,
      asset: run.asset, timeframe: run.timeframe,
      date_start: run.date_start, date_end: run.date_end,
      metrics: run.metrics || run.best_metrics || run.risk_metrics || null,
      created_at: run.created_at,
    };
    return {
      currentStrategy: mem.currentStrategy && {
        id: mem.currentStrategy.id,
        name: mem.currentStrategy.name,
        market: mem.currentStrategy.market,
        strategy_type: mem.currentStrategy.strategy_type,
      },
      lastBacktest:     trim(mem.runs[mem.lastBacktestRunId]),
      lastOptimization: trim(mem.runs[mem.lastOptimizationRunId]),
      lastStress:       trim(mem.runs[mem.lastStressRunId]),
      currentPage: parseHash().page || 'vault',
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // Public surface
  // ────────────────────────────────────────────────────────────────────
  const api = {
    // subscribe / read
    subscribe, snapshot,
    getCurrentStrategy, getCurrentStrategyId,
    getLastBacktestRunId, getLastOptimizationRunId, getLastStressRunId,
    getRun,
    // mutate
    setCurrentStrategy,
    setLastBacktestRun, setLastOptimizationRun, setLastStressRun,
    cacheRun, clear,
    // persistence
    hydrateFromSupabase,
    saveBacktestRun, saveOptimizationRun, saveStressRun,
    fetchBacktestRun, fetchOptimizationRun, fetchStressRun,
    // URL routing
    parseHash, setHash, hydrateFromURL,
    // AI context
    serialize,
  };

  if (root) {
    root.GQ = root.GQ || {};
    root.GQ.state = api;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
